import { CreateKycDocumentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/CreateKycDocumentDrawer/CreateKycDocumentDrawer.tsx';
import {
	FormTemplate,
	KYC_FORM_FIELDS,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { KycFormScopeProvider } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycFormScopeContext';
import { useOptionalMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import { TaxComplianceCategory } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/components/TaxComplianceCategory.tsx';
import { KycFormActionDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/components/KycFormActionDrawer/KycFormActionDrawer.tsx';
import { TemplatePickerModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/components/TemplatePicker/TemplatePickerModal.tsx';
import { ArchivedBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/styled.ts';
import { useTaxAndCompliance } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/useTaxAndCompliance.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { uploadFile } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { CustomDetailCategory, CustomDetailEnum } from '@dealstack/shared/lib/generated';
import { Button, Flex, Text, Tooltip } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';

type TaxFormValues = {
	individualFormsTemplates: FormTemplate[];
	pivFormsTemplates: FormTemplate[];
};

interface Props {
	organizationId?: string;
}

export const TaxAndCompliance = ({ organizationId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const mipManageController = useOptionalMIPManageContext();
	const mipId = mipManageController?.mipId;
	const [showArchived, setShowArchived] = useState(false);

	const {
		individualFormsTemplates,
		pivFormsTemplates,
		isLoading,
		handleAddFormTemplate,
		handleUseTemplate,
		handleDeleteTemplate,
		handleUpdateTemplate,
		handleArchiveTemplate,
		pendingFormAction,
		instanceCount,
		isFormActionPending,
		handleConfirmFormAction,
		handleCancelFormAction,
		hasPermission,
	} = useTaxAndCompliance({ mipId, organizationId, showArchived });

	const uploadFileMutation = useMutation({ mutationFn: uploadFile });
	const [activeTab, setActiveTab] = useState<ENTITY_TABS>(ENTITY_TABS.INDIVIDUAL);
	const [selectedCategory, setSelectedCategory] = useState<CustomDetailCategory | null>(null);

	const handleCategoryUploadClick = (category: CustomDetailCategory | null) => {
		setSelectedCategory(category);
	};

	// Separate from `selectedCategory`, which drives the upload drawer: null is a real section
	// here, so "which section" cannot double as "is the picker open".
	const [templateSection, setTemplateSection] = useState<{
		category: CustomDetailCategory | null;
	} | null>(null);

	const form = useForm<TaxFormValues>({
		mode: 'all',
		values: {
			individualFormsTemplates,
			pivFormsTemplates,
		},
	});

	const handleDrawerClose = () => {
		setSelectedCategory(null);
		uploadFileMutation.reset();
	};

	const tabsConfig: MantineTabProps<ENTITY_TABS>[] = [
		{
			label: tMipManage('kyc_settings.label.individual'),
			value: ENTITY_TABS.INDIVIDUAL,
			icon: 'information',
		},
		{
			label: tMipManage('kyc_settings.label.vehicle'),
			value: ENTITY_TABS.INVESTMENT_VEHICLE,
			icon: 'derived_commuted_structure',
		},
	];

	const currentTemplates =
		activeTab === ENTITY_TABS.INDIVIDUAL
			? form.watch(KYC_FORM_FIELDS.individualFormsTemplates)
			: form.watch(KYC_FORM_FIELDS.pivFormsTemplates);

	const fieldArrayName =
		activeTab === ENTITY_TABS.INDIVIDUAL
			? KYC_FORM_FIELDS.individualFormsTemplates
			: KYC_FORM_FIELDS.pivFormsTemplates;

	const currentType =
		activeTab === ENTITY_TABS.INDIVIDUAL
			? CustomDetailEnum.INDIVIDUAL
			: CustomDetailEnum.VEHICLE;

	const templatesByCategory = useMemo(() => {
		return {
			[CustomDetailCategory.TAX_RESIDENCY]: currentTemplates.filter(
				(t) => t.category === CustomDetailCategory.TAX_RESIDENCY
			),
			[CustomDetailCategory.FATCA]: currentTemplates.filter(
				(t) => t.category === CustomDetailCategory.FATCA
			),
			[CustomDetailCategory.CRS]: currentTemplates.filter(
				(t) => t.category === CustomDetailCategory.CRS
			),
			[CustomDetailCategory.GDPR]: currentTemplates.filter(
				(t) => t.category === CustomDetailCategory.GDPR
			),
			other: currentTemplates.filter(
				(t) =>
					!t.category ||
					![
						CustomDetailCategory.TAX_RESIDENCY,
						CustomDetailCategory.FATCA,
						CustomDetailCategory.CRS,
						CustomDetailCategory.GDPR,
					].includes(t.category)
			),
		};
	}, [currentTemplates]);

	if (isLoading) {
		return <InlineLoader />;
	}

	const archiveButton = hasPermission ? (
		<Tooltip
			label={
				showArchived
					? tMipManage('settings.tax.show_active')
					: tMipManage('settings.tax.show_archived')
			}
		>
			<Button
				onClick={() => setShowArchived(!showArchived)}
				variant="light"
				color="gray"
				bg="gray.2"
				c="dark.8"
				mr="0.5rem"
				fw={400}
			>
				<Icon variant="archivebox" size="vlg" />
			</Button>
		</Tooltip>
	) : undefined;

	return (
		<KycFormScopeProvider organizationId={organizationId}>
			<FormProvider {...form}>
				<MantineTabs
					tabs={tabsConfig}
					panels={[]}
					onlyDividerBorder={true}
					value={activeTab}
					onTabChange={(value) => setActiveTab(value as ENTITY_TABS)}
					rightContent={archiveButton}
				>
					<Flex direction="column" p="1rem">
						{showArchived && (
							<ArchivedBanner
								justify="space-between"
								align="center"
								p="1rem"
								mb="1rem"
							>
								<Text size="B6Bold" c="dark.8">
									{tMipManage('settings.tax.viewing_archived')}
								</Text>
								<Button
									onClick={() => setShowArchived(false)}
									variant="transparent"
									c="blue.6"
									p={0}
								>
									{tMipManage('settings.tax.exit_archive_view')}
								</Button>
							</ArchivedBanner>
						)}

						<TaxComplianceCategory
							title={tMipManage('settings.tax.tax.label')}
							description={tMipManage('settings.tax.tax.desc')}
							templates={templatesByCategory[CustomDetailCategory.TAX_RESIDENCY]}
							fieldArrayName={fieldArrayName}
							allTemplates={currentTemplates}
							uploadFileMutation={uploadFileMutation}
							invalidTypeMessage={tMipManage(
								'contracts.table.errors.invalid_file_type'
							)}
							category={CustomDetailCategory.TAX_RESIDENCY}
							onCategoryUploadClick={handleCategoryUploadClick}
							onUseTemplateClick={(category) => setTemplateSection({ category })}
							onDeleteTemplate={handleDeleteTemplate}
							onUpdateTemplate={handleUpdateTemplate}
							onArchiveTemplate={handleArchiveTemplate}
							hasPermission={hasPermission}
							showArchived={showArchived}
						/>

						<TaxComplianceCategory
							title={tMipManage('settings.tax.fatca.label')}
							description={tMipManage('settings.tax.fatca.desc')}
							templates={templatesByCategory[CustomDetailCategory.FATCA]}
							fieldArrayName={fieldArrayName}
							allTemplates={currentTemplates}
							uploadFileMutation={uploadFileMutation}
							invalidTypeMessage={tMipManage(
								'contracts.table.errors.invalid_file_type'
							)}
							category={CustomDetailCategory.FATCA}
							onCategoryUploadClick={handleCategoryUploadClick}
							onUseTemplateClick={(category) => setTemplateSection({ category })}
							onDeleteTemplate={handleDeleteTemplate}
							onUpdateTemplate={handleUpdateTemplate}
							onArchiveTemplate={handleArchiveTemplate}
							hasPermission={hasPermission}
							showArchived={showArchived}
						/>

						<TaxComplianceCategory
							title={tMipManage('settings.tax.crs.label')}
							description={tMipManage('settings.tax.crs.desc')}
							templates={templatesByCategory[CustomDetailCategory.CRS]}
							fieldArrayName={fieldArrayName}
							allTemplates={currentTemplates}
							uploadFileMutation={uploadFileMutation}
							invalidTypeMessage={tMipManage(
								'contracts.table.errors.invalid_file_type'
							)}
							category={CustomDetailCategory.CRS}
							onCategoryUploadClick={handleCategoryUploadClick}
							onUseTemplateClick={(category) => setTemplateSection({ category })}
							onDeleteTemplate={handleDeleteTemplate}
							onUpdateTemplate={handleUpdateTemplate}
							onArchiveTemplate={handleArchiveTemplate}
							hasPermission={hasPermission}
							showArchived={showArchived}
						/>

						<TaxComplianceCategory
							title={tMipManage('settings.tax.gdpr.label')}
							description={tMipManage('settings.tax.gdpr.desc')}
							templates={templatesByCategory[CustomDetailCategory.GDPR]}
							fieldArrayName={fieldArrayName}
							allTemplates={currentTemplates}
							uploadFileMutation={uploadFileMutation}
							invalidTypeMessage={tMipManage(
								'contracts.table.errors.invalid_file_type'
							)}
							category={CustomDetailCategory.GDPR}
							onCategoryUploadClick={handleCategoryUploadClick}
							onUseTemplateClick={(category) => setTemplateSection({ category })}
							onDeleteTemplate={handleDeleteTemplate}
							onUpdateTemplate={handleUpdateTemplate}
							onArchiveTemplate={handleArchiveTemplate}
							hasPermission={hasPermission}
							showArchived={showArchived}
						/>

						<TaxComplianceCategory
							title={tMipManage('settings.tax.other.label')}
							description={tMipManage('settings.tax.other.desc')}
							templates={templatesByCategory.other}
							fieldArrayName={fieldArrayName}
							allTemplates={currentTemplates}
							uploadFileMutation={uploadFileMutation}
							invalidTypeMessage={tMipManage(
								'contracts.table.errors.invalid_file_type'
							)}
							category={null}
							onCategoryUploadClick={handleCategoryUploadClick}
							onUseTemplateClick={(category) => setTemplateSection({ category })}
							onDeleteTemplate={handleDeleteTemplate}
							onUpdateTemplate={handleUpdateTemplate}
							onArchiveTemplate={handleArchiveTemplate}
							hasPermission={hasPermission}
							showArchived={showArchived}
							showDivider={false}
						/>
					</Flex>
				</MantineTabs>

				{templateSection && (
					<TemplatePickerModal
						opened
						investorType={currentType}
						category={templateSection.category}
						onAdd={async (template) =>
							await handleUseTemplate({
								globalDocumentTemplateId: template.id,
								name: template.name,
								type: currentType,
								category: templateSection.category,
							})
						}
						onClose={() => setTemplateSection(null)}
					/>
				)}

				{!!uploadFileMutation.data?.payload?.id && (
					<CreateKycDocumentDrawer
						addFormTemplate={handleAddFormTemplate}
						onClose={handleDrawerClose}
						file={uploadFileMutation.data.payload}
						opened={!!uploadFileMutation.data?.payload?.id}
						type={currentType}
						category={selectedCategory}
					/>
				)}

				<KycFormActionDrawer
					key={pendingFormAction?.templateId}
					action={pendingFormAction?.action ?? null}
					instanceCount={instanceCount}
					loading={isFormActionPending}
					onConfirm={handleConfirmFormAction}
					onClose={handleCancelFormAction}
				/>
			</FormProvider>
		</KycFormScopeProvider>
	);
};
